<script>
   /****************************************************************/
   /* PlotScores                                                   */
   /* --------------------                                         */
   /* shows plot with PCA scores                                   */
   /****************************************************************/

   import { ColormapLegend, Segments, Legend } from 'svelte-plots-basic/2d';
   import { Vector, vector } from 'mdatools/arrays';

   import { getAxisTitle, getColorGroups, getResLegend, AXIS_COLOR, MAX_LABEL_SYMBOLS, MARKERS, MARGINS } from './Defaults.js';
   import { pcares_get } from '@mdatools-apps/wasm/pca.js';
   import { csvRow, downloadCSV } from '../utils.js';

   import Axes from './Axes.svelte';
   import { plotSize, legendNames } from './layout.js';
   import PlotSeries from './PlotSeries.svelte';
   import HotellingEllipse from './HotellingEllipse.svelte';
   import ConfidenseEllipses from './ConfidenseEllipses.svelte';


   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
      alpha = 0.05,                  // confidence level for Hotelling ellipse
   } = $props();

   // get the parameters needed for the plot
   const data = $derived(props && props.data ? props.data : null);
   const res = $derived(props && props.res ? props.res : null);

   // get results and datasets (second objects will be available only if user selected 'both')
   const resIDs = $derived(res && params ? (params.resID === 'both' ? Object.keys(res) : [params.resID]) : null);
   const resobj1 = $derived(resIDs ? res[resIDs[0]] : null);
   const resobj2 = $derived(resIDs && resIDs.length > 1 ? res[resIDs[1]] : null);
   const dataset1 = $derived(resIDs ? data[resIDs[0]] : null);

   /**
    * Computes min, max values for axis limits as well as 10% of their difference for x or y axis.
    *
    * @param showEllipse - logical, show or not Hotelling ellips.
    * @param confEllipse - string, confidence level for the confidence ellips (or 'none').
    * @param rangeS - range for score values.
    * @param rangeE - range for Hotelling ellipse coordinates.
    * @param rangeCE - range for confidence ellipse coordinates.
    */
   function getLim(showEllipse, confEllipse, rangeS1, rangeS2, rangeE, rangeCE) {
      if (!rangeS1) return null;

      // compute ranges for score values
      let vMin = rangeS1[0];
      let vMax = rangeS1[1];

      // adjust ranges if T2 ellipse is shown
      if (rangeS2) {
         vMin = Math.min(vMin, rangeS2[0]);
         vMax = Math.max(vMax, rangeS2[1]);
      }

      // adjust ranges if T2 ellipse is shown
      if (showEllipse && rangeE) {
         vMin = Math.min(vMin, rangeE[0]);
         vMax = Math.max(vMax, rangeE[1]);
      }

      // adjust ranges if confidence ellipses are shown
      if (confEllipse !== 'none' && rangeCE) {
         vMin = Math.min(vMin, rangeCE[0]);
         vMax = Math.max(vMax, rangeCE[1]);
      }

      // compute limits based on the ranges
      const dv = (vMax - vMin) * 0.10;
      return {min: vMin, max: vMax, d: dv};
   }

   /**
    * Gets vector with values for particular outcome and component from the result object
    * @param {Object} r - result object.
    * @param {Number} a - component index (from 1);
    */
   function getValues(r, a) {
      if (!r || !a || a < 1 || a > r.ncomp) return;
      return new Vector(pcares_get(r, 'scores', a))
   }

   // ranges of values to define coordinate limits
   let xRangeS1 = $state(), yRangeS1 = $state();      // scores for series 1
   let xRangeS2 = $state(), yRangeS2 = $state();      // scores for series 2 if available
   let xRangeE = $state(), yRangeE = $state();        // hotelling ellipse
   let xRangeCE = $state(), yRangeCE = $state();      // confidence ellipse


   // reactive expression to generate x-indices for line and bar plots
   let rowIndices1 = $derived(resobj1 ? vector(resobj1.objindices).apply(v => v + 1) : null);
   let rowIndices2 = $derived(resobj2 ? vector(resobj2.objindices).apply(v => v + 1) : null);

   // labels
   let labels1 = $derived(resobj1 ? resobj1.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : null);
   let labels2 = $derived(resobj2 ? resobj2.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : null);

   // reactive expression to track changes in "res" and "comp[0]" (x-axis)
   let compXValues1 = $derived(getValues(resobj1, params.comp1));
   let compXValues2 = $derived(getValues(resobj2, params.comp1));

   let xValues1 = $derived(params.plotType === 'p' ? compXValues1 : rowIndices1);
   let xValues2 = $derived(params.plotType === 'p' ? compXValues2 : rowIndices2);

   // reactive expression to track chenges in "res" and "comp[1]" (y-axis)
   let compY = $derived(params.comp2 ? params.comp2 : null);
   let yValues1 = $derived(getValues(resobj1, compY));
   let yValues2 = $derived(resobj2 ? getValues(resobj2, compY) : null);


   // reactive expression to get colors of the elements
   let col1 = $derived(getColorGroups(dataset1, resobj1, params.colorBy, params.colorByVar, resIDs[0]));

   // the drawn size of the plot, for the class names cut to fit their legend cells
   let size = $state([0, 0]);
   const legendLabels = $derived(col1?.byClass ? legendNames(col1.labels, size, MARGINS, true) : col1?.labels);

   let pch1 = $derived(MARKERS[resIDs[0]]);

   let col2 = $derived(resIDs.length > 1 ? getColorGroups(null, resobj2, 'none', 0, resIDs[1]) : null);
   let pch2 = $derived(resIDs.length > 1 ? MARKERS[resIDs[1]] : null);

   // reactive expression to generate axis labels
   let xLabel = $derived(params.plotType === 'p' ? getAxisTitle(resobj1, params.comp1) : 'Objects');
   let yLabel = $derived(getAxisTitle(resobj1, params.comp2));

   // reactive expression to compute x-axis limits
   let lx = $derived(getLim(params.showEllipse, params.confEllipse, xRangeS1,  xRangeS2, xRangeE, xRangeCE));
   let limX = $derived(lx ? [lx.min - lx.d, lx.max + lx.d] : [0, 1]);

   // reactive expression to compute x-axis limits
   let ly = $derived(getLim(params.showEllipse, params.confEllipse, yRangeS1, yRangeS2, yRangeE, yRangeCE));
   let limY = $derived(ly ?[ly.min - ly.d, ly.max + ly.d * (1 + (params.colorBy !== 'none'))] : [0, 1]);

   // reactive expression to generate plot title
   let title = $derived(
      (params.plotType ===  'l' || params.colorBy === 'none' || !col1) ? 'Scores' :
      params.colorBy === 'groups' ? 'Scores (colored by groups)' :
         'Scores (colored by ' + col1.varname + (dataset1.varvaluesUnits ? ' ' + dataset1.varvaluesUnits : '') + ')'
   );


   // method for creating CSV version of the plot data
   function downloadCSVCallback() {
      // 'data' is a map of datasets keyed by result id, not a dataset - reading 'sep' from it
      // gave undefined and every export fell back to a comma, whatever the file used
      const { sep, dec } = dataset1;

      // elements of the first (main) subset
      let l = resobj1.objlabels;
      let x = Array.from(xValues1.v);
      let y = Array.from(yValues1.v);
      let s = Array(x.length).fill(resIDs[0]);
      let g = resobj1.classes;

      // elements of the second subset if available
      if (xValues2 !== null && yValues2 !== null) {
         const n2 = xValues2.length;
         s = [...s, ...Array(n2).fill(resIDs[1])];
         l = [...l, ...resobj2.objlabels];
         x = [...x, ...Array.from(xValues2.v)];
         y = [...y, ...Array.from(yValues2.v)];
         g = null;
      }

      let out = '';
      if (params.colorBy === 'groups' && g) {
         out += csvRow(['Set', 'Labels', 'Groups', xLabel, yLabel], sep, dec);
         for (let i = 0; i < x.length; i++) {
            out += csvRow([s[i], l[i], g[i], x[i], y[i]], sep, dec);
         }
      } else {
         out += csvRow(['Set', 'Labels', xLabel, yLabel], sep, dec);
         for (let i = 0; i < x.length; i++) {
            out += csvRow([s[i], l[i], x[i], y[i]], sep, dec);
         }
      }

      downloadCSV(out, 'plot-scores');
   }


   // legend
   const legendItems = $derived(getResLegend(resIDs));

   // reset ranges if second series is not shown
   $effect(() => {
      if (!xValues2) {
         xRangeS2 = null;
      }
      if (!yValues2) {
         yRangeS2 = null;
      }
   })
</script>

{#if xValues1 && yValues1 }
<!-- 'display: contents' leaves the layout to Axes -->
<div style="display: contents" {@attach plotSize(v => size = v)}>
<Axes {title} {limX} {limY} {xLabel} {yLabel} {downloadCSVCallback} fileName='plot-scores'>

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   {#if params.plotType === 'p'}
   <Segments xStart={[0]} xEnd={[0]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor={AXIS_COLOR} />
   {/if}

   <!-- T2 ellipse -->
   {#if params.showEllipse && params.plotType === 'p'}
   <HotellingEllipse
      {alpha}
      resobj={resobj1}
      comp={[params.comp1, params.comp2]}
      bind:xRange={xRangeE}
      bind:yRange={yRangeE}
   />
   {/if}

   {#if params.colorBy === 'groups' && params.confEllipse !== 'none' && params.plotType === 'p' && col1}
   <ConfidenseEllipses
      xValues={xValues1}
      yValues={yValues1}
      confLevel={Number.parseInt(params.confEllipse)/100}
      groups={resobj1.classes}
      groupNames={resobj1.classNames}
      groupColors={col1.map}
      bind:xRange={xRangeCE}
      bind:yRange={yRangeCE}
   />
   {/if}


   <!-- plot series 1 -->
   <PlotSeries
      xValues={xValues1}
      yValues={yValues1}
      labels={labels1}
      marker={pch1}
      nBars={resIDs.length}
      barNum={1}
      plotType={params.plotType}
      showLabels={params.showLabels}
      colors={col1.colors}
      mainColor={col1.mainColor}
      faceColor={col1.faceTColor}
      bind:xRange={xRangeS1}
      bind:yRange={yRangeS1}
      {selected}
      {callbacks}
   />

   <!-- plot series 2 -->
   {#if xValues2 && yValues2}
   <PlotSeries
      xValues={xValues2}
      yValues={yValues2}
      labels={labels2}
      {selected}
      {callbacks}
      nBars={resIDs.length}
      barNum={2}
      plotType={params.plotType}
      showLabels={params.showLabels && params.plotType === 'p'}
      marker={pch2}
      colors={col2.colors}
      mainColor={col2.mainColor}
      faceColor={col2.faceTColor}
      bind:xRange={xRangeS2}
      bind:yRange={yRangeS2}
   />
   {/if}

   <!-- colormap legend-->
   {#if col1}
   <ColormapLegend show={col1.breaks !== null} colmap={col1.map} breaks={col1.breaks} labels={legendLabels}/>
   {/if}

   <Legend items={legendItems} show={params.colorBy === 'none'}/>

</Axes>
</div>
{/if}
